import { useAuth } from '../auth/AuthContext';
import { roleLabel } from '../auth/roles';
import { API_BASE_URL } from '../api/client';

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Session chip, reading the shared auth context (one `/auth/me` per boot — the
 * router guard and the pages use the same query cache entry). Outcomes:
 *  - `AUTH_DISABLED` -> nothing rendered: local dev / e2e has no login to show.
 *  - anonymous       -> nothing: `<ProtectedRoute>` has already sent the
 *                       visitor to `/login`.
 *  - authenticated   -> avatar initial, e-mail, role badge and a Log out link.
 *
 * The Log out control is an `<a>`, not a client-side `navigate()`: the session
 * must be destroyed on the server and the SPA fully re-mounted afterwards.
 */
export function UserChip() {
  const { status, user, role, authDisabled } = useAuth();

  if (authDisabled || status !== 'ready' || !user) return null;

  const initial = user.name[0]?.toUpperCase() ?? user.email[0]?.toUpperCase() ?? '?';

  return (
    <div className="user" title={user.email} data-testid="user-chip">
      <span className="user__avatar" aria-hidden="true">
        {initial}
      </span>
      <span className="user__meta">
        <span className="user__email">{user.email}</span>
        {role ? (
          <span className="badge badge--neutral" data-testid="user-role">
            {roleLabel(role)}
          </span>
        ) : null}
      </span>
      {/* Full navigation on purpose — see the comment on `auth.logout`. */}
      <a className="user__logout" href={`${API_BASE_URL}/auth/logout`} aria-label="Выйти" title="Выйти">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M14 4h4a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 18 20h-4" {...ICON_STROKE} />
          <path d="M9.5 8 5.5 12l4 4" {...ICON_STROKE} />
          <path d="M5.5 12h9" {...ICON_STROKE} />
        </svg>
        <span>Выйти</span>
      </a>
    </div>
  );
}
